import { useState, useMemo } from "react";
import PageHeader, { Page } from "@/components/layout/PageHeader";
import { EmptyState, Notice, StatusPill, DemoBadge } from "@/components/common/States";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import JobDetailModal from "@/components/common/JobDetailModal";
import { useStudio, useLookups } from "@/store/StudioContext";
import { groupItemsByDate } from "@/lib/dateUtils";
import { formatResolutionLabel } from "@/domain/resolutionLabels";
import { Eye, Calendar } from "lucide-react";
import { t } from "@/i18n";

const fmt = (v) => (v == null ? null : v < 0.1 ? v.toFixed(3) : v.toFixed(2));

const CostCell = ({ job }) => {
  if (job.costReal != null) {
    return <span className="font-mono text-xs text-[#8fcb8b]">{t.tasks.costConfirmed(fmt(job.costReal))}</span>;
  }
  if (job.costEstimate != null) {
    return <span className="font-mono text-xs text-[#f5b544]">{t.tasks.costEstimated(fmt(job.costEstimate))}</span>;
  }
  return <span className="font-mono text-xs text-[#a3a3a3]">{t.tasks.costUnknown}</span>;
};

const TasksPage = () => {
  const { state, dispatch } = useStudio();
  const { projectById, sceneById, assetById } = useLookups();
  const [confirmClear, setConfirmClear] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  const jobs = state.jobs;
  const blockedCount = jobs.filter((j) => j.status === "blocked").length;
  const hasRealJobs = jobs.some((j) => !j.isDemo && j.status !== "blocked");
  const lang = state.onboarding?.language || "es";

  // Agrupamiento cronológico estilo Freepik por día (descendente)
  const groupedJobs = useMemo(() => {
    return groupItemsByDate(jobs, (j) => j.createdAt, lang);
  }, [jobs, lang]);

  const noteFor = (j) => {
    if (j.status === "blocked") return j.note || t.tasks.blockedReasonNoKey;
    if (j.status === "uncertain") return t.tasks.uncertainReason;
    if (j.status === "failed") return j.errorDetail || j.note;
    return j.note;
  };

  return (
    <Page testId="tasks-page">
      <PageHeader
        title={t.tasks.title}
        subtitle={t.tasks.subtitle}
        right={blockedCount > 0 && (
          <button data-testid="tasks-clear-blocked" className="v-btn-ghost v-btn-sm" onClick={() => setConfirmClear(true)}>
            {t.tasks.clear}
          </button>
        )}
      />

      {blockedCount > 0 && !hasRealJobs && (
        <Notice tone="warn" testId="tasks-provider-notice">{t.tasks.countHint}</Notice>
      )}

      <div className="mt-6">
        {jobs.length === 0 ? (
          <EmptyState title={t.tasks.empty} body={t.tasks.emptyBody} testId="tasks-empty" />
        ) : (
          <div className="space-y-8">
            {groupedJobs.map((group) => (
              <div key={group.key} data-testid={`tasks-group-${group.key}`} className="space-y-3">
                {/* ── Encabezado de grupo por fecha (estilo Freepik) ── */}
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-[#f5b544]" />
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-[#ededed]">
                      {group.title}
                    </h3>
                  </div>
                  <span className="text-[11px] font-mono text-[#8a8a8a] bg-[#1a1a1a] px-2 py-0.5 rounded border border-[#2a2a2a]">
                    {group.items.length} {group.items.length === 1 ? "generación" : "generaciones"}
                  </span>
                  <div className="flex-1 h-px bg-[#262626]" />
                </div>

                {/* ── Tabla de tareas del día ── */}
                <div className="v-panel overflow-hidden border border-[#2a2a2a]">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs text-[#a3a3a3]">
                      <tr className="border-b border-[#303030]">
                        <th className="px-4 py-3 font-medium">{t.common.type}</th>
                        <th className="px-4 py-3 font-medium">{t.common.prompt}</th>
                        <th className="hidden px-4 py-3 font-medium md:table-cell">{t.common.project}</th>
                        <th className="hidden px-4 py-3 font-medium lg:table-cell">{t.tasks.model}</th>
                        <th className="hidden px-4 py-3 font-medium lg:table-cell">{t.tasks.cost}</th>
                        <th className="px-4 py-3 font-medium">{t.common.status}</th>
                        <th className="px-4 py-3 font-medium text-right">Detalles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.items.map((j) => (
                        <tr
                          key={j.id}
                          data-testid={`task-row-${j.id}`}
                          className="border-b border-[#262626] align-top last:border-0 hover:bg-[#181818]/60 transition-colors"
                        >
                          <td className="px-4 py-3 whitespace-nowrap text-xs font-medium text-[#ededed]">
                            {t.tasks.kinds[j.kind] || j.kind}
                          </td>
                          <td className="px-4 py-3">
                            <div className="line-clamp-2 max-w-md text-[#d8d4cd]">{j.prompt || "—"}</div>
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[#a3a3a3]">
                              <span className="font-mono text-[#737373]">{j.backendJobId || j.id}</span>
                              {sceneById[j.sceneId] && <span>· {sceneById[j.sceneId].title}</span>}
                              {j.isDemo && <DemoBadge label={t.tasks.demoTag} />}
                              {j.isSimulation && <DemoBadge label={t.tasks.simTag} />}
                            </div>
                            {noteFor(j) && (
                              <div className={`mt-1 text-xs ${j.status === "uncertain" ? "text-[#f5b544]" : "text-[#a3a3a3]"}`}>
                                {noteFor(j)}
                              </div>
                            )}
                          </td>
                          <td className="hidden px-4 py-3 text-[#a3a3a3] md:table-cell">
                            {projectById[j.projectId]?.name || t.common.noProject}
                          </td>
                          <td className="hidden px-4 py-3 font-mono text-xs text-[#a3a3a3] lg:table-cell">
                            {j.model || t.tasks.modelUnknown}
                          </td>
                          <td className="hidden px-4 py-3 lg:table-cell">
                            <CostCell job={j} />
                          </td>
                          <td className="px-4 py-3">
                            <StatusPill status={j.status} map={t.tasks.status} />
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            <button
                              type="button"
                              data-testid={`task-details-btn-${j.id}`}
                              onClick={() => setSelectedJob(j)}
                              className="v-btn-ghost v-btn-xs inline-flex items-center gap-1.5 text-xs text-[#f5b544] hover:text-[#ffd17d] hover:border-[#f5b544]/40"
                              title="Ver características y detalles completos de la generación"
                            >
                              <Eye size={12} />
                              <span>Ver detalles</span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Modal "Ver detalles" ── */}
      <JobDetailModal
        job={selectedJob}
        open={Boolean(selectedJob)}
        onOpenChange={(isOpen) => !isOpen && setSelectedJob(null)}
        assetById={assetById}
        projectById={projectById}
        sceneById={sceneById}
        lang={lang}
      />

      <ConfirmDialog
        open={confirmClear}
        onOpenChange={setConfirmClear}
        title={t.tasks.clear}
        body={typeof t.tasks.clearConfirm === "function" ? t.tasks.clearConfirm(blockedCount) : t.tasks.clearConfirm}
        onConfirm={() => dispatch({ type: "JOB_REMOVE_BLOCKED" })}
      />
    </Page>
  );
};

export default TasksPage;
